import { useQueryClient } from "@tanstack/react-query";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SwitchRadioButton } from "../../components";
import { EndPoints } from "../../Constants/endpoints";
import { useGetData, usePostData } from "../../hooks";
import { useToast } from "../../Services/ToastContext";

interface INotificationSetting {
  AllNotifications?: boolean;
  AppUpdates?: boolean;
  AppointmentNotification?: boolean;
  Sound?: boolean;
}

const NotificationsSetting = () => {
  const { showToast } = useToast();

  const { t } = useTranslation();
  const { data, isSuccess } = useGetData<INotificationSetting>(
    EndPoints.GetNotificationSetting,
  );

  const { mutate } = usePostData(EndPoints.EditNotificationSetting);
  const queryClient = useQueryClient();

  const [notificationSetting, setNotificationSetting] = useState<
    INotificationSetting | undefined
  >(data?.Data);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleChange = (value: boolean, name: string) => {
    setNotificationSetting((prevState: INotificationSetting | undefined) => {
      const updatedState = { ...(prevState ?? {}), [name]: value };
 
      const { Sound, AppointmentNotification, AppUpdates } = updatedState;
      const allChecked = Sound && AppointmentNotification && AppUpdates;
      updatedState.AllNotifications = allChecked;

      return updatedState;
    });
 
    const newState = { ...(notificationSetting ?? {}), [name]: value };
    const { Sound, AppointmentNotification, AppUpdates } = newState;
    newState.AllNotifications = Sound && AppointmentNotification && AppUpdates;

    submitChanges(newState);
  };

  const submitChanges = (settings: INotificationSetting) => { 
    if (isUpdating || _.isEqual(data?.Data, settings)) {
      return;
    }

    setIsUpdating(true);
    mutate(settings as any, {
      onSuccess() {
        showToast(t("notification_updated_successfully"), "success");
        queryClient.refetchQueries({
          queryKey: [EndPoints.GetNotificationSetting],
          exact: false,
        });
        setIsUpdating(false);
      },
      onError() {
        showToast(t("an_error_occurred_please_try_again"), "error");
        setIsUpdating(false);
      },
    });
  };
 
  const areAllNotificationsEnabled = (
    settings: INotificationSetting | undefined,
  ) => {
    if (!settings) return false;
    return !!(
      settings.Sound &&
      settings.AppointmentNotification &&
      settings.AppUpdates
    );
  };

  useEffect(() => {
    if (isSuccess && data?.Data) {
      setNotificationSetting(data.Data);
      setIsUpdating(false);
    }
  }, [isSuccess, data]);

  return (
    <div className="flex flex-col gap-6">
      <SwitchRadioButton
        checked={areAllNotificationsEnabled(notificationSetting)}
        onChange={(event) => {
          const isChecked = event?.target?.checked;
          const updatedSettings = Object.keys(notificationSetting ?? {}).reduce(
            (acc, key) => {
              acc[key as keyof INotificationSetting] = isChecked;
              return acc;
            },
            {} as INotificationSetting,
          );

          setNotificationSetting(updatedSettings);
          submitChanges(updatedSettings);
        }}
        text={t("turn_on_all_notifications")}
      />
      <div className="flex flex-col gap-2">
        <SwitchRadioButton
          className="cursor-pointer"
          checked={notificationSetting?.Sound}
          onChange={(event) => {
            handleChange(event?.target?.checked, "Sound");
          }}
          text={t("sound")}
          isWithBg
        />

        <SwitchRadioButton
          checked={notificationSetting?.AppointmentNotification}
          onChange={(event) => {
            handleChange(event?.target?.checked, "AppointmentNotification");
          }}
          text={t("appointment_requests")}
          isWithBg
        />
        <SwitchRadioButton
          checked={notificationSetting?.AppUpdates}
          onChange={(event) => {
            handleChange(event?.target?.checked, "AppUpdates");
          }}
          text={t("app_updates")}
          isWithBg
        />
      </div>
    </div>
  );
};

export default NotificationsSetting;
